import React, { memo, useEffect, useRef } from "react";
import ResearchContent from "./ResearchContent";
import DocumentPagination from "./DocumentPagination";
import Typography from "../../components/Typography/Typography";
import Loader from "../../components/Loader/Loader";
import gsap from "gsap";
import animatedResponses from "./animatedResponses";

const phrases = [
  "Cercando tra milioni di documenti accademici...",
  "Analizzando una vasta raccolta di fonti attendibili...",
  "Esplorando archivi digitali e biblioteche online...",
  "Setacciando banche dati universitarie...",
  "Frugando tra articoli scientifici e paper...",
  "Scavando tra ricerche e pubblicazioni accademiche...",
  "Trovando le fonti più rilevanti per la tua tesi...",
  "Selezionando studi aggiornati e pertinenti...",
  "Raccogliendo dati utili da fonti autorevoli...",
  "Preparando la risposta migliore sulla base delle fonti trovate...",
];

function AnimatedSearchText({ isMobile }) {
  const phraseContainerRef = useRef(null);
  const indexRef = useRef(Math.floor(Math.random() * phrases.length));

  // Splits text into span-wrapped chars
  const splitToSpans = (text, container) => {
    const chars = Array.from(text);
    container.innerHTML = "";
    chars.forEach((char) => {
      const span = document.createElement("span");
      span.textContent = char;
      span.style.opacity = "0";
      container.appendChild(span);
    });
  };

  useEffect(() => {
    const container = phraseContainerRef.current;
    if (!container) return;

    const showPhrase = (text) => {
      splitToSpans(text, container);
      gsap.to(container.querySelectorAll("span"), {
        opacity: 1,
        duration: 0.4,
        stagger: 0.02,
        ease: "power1.out",
      });
    };

    // Initial render
    showPhrase(phrases[indexRef.current]);

    const swapLines = () => {
      const nextIndex = (indexRef.current + 1) % phrases.length;
      indexRef.current = nextIndex;
      showPhrase(phrases[nextIndex]);
    };

    const id = setInterval(swapLines, 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex items-center gap-3 relative overflow-hidden h-6">
      <Loader size={isMobile ? 20 : 16} />
      <div className="relative w-full h-6 overflow-hidden">
        <div
          ref={phraseContainerRef}
          className="absolute top-0 left-0 w-full whitespace-pre text-sm text-gray-600 leading-6"
        />
      </div>
    </div>
  );
}

export default memo(function ResearchChatView({
  selectedChat,
  selectedDocumentsChat,
  searchResponses,
  currentPrompt,
  containerRef,
  latestSearchResultRef,
  handleSearch,
  loading
}) {
  const isMobile = window.innerWidth < 768;

  // Track the latest response ID to determine which response should animate
  const latestResponseRef = useRef(null);

  useEffect(() => {
    // When new responses arrive, update the latest response ID
    if (searchResponses && searchResponses.length > 0 && !loading) {
      const latestResponse = searchResponses[searchResponses.length - 1];
      if (latestResponse.aiContent) {
        latestResponseRef.current = latestResponse.aiContent.id ||
          `response-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
    }
  }, [searchResponses, loading]);

  if (selectedChat) {
    return (
      <div className="flex md:flex-row flex-col-reverse w-full gap-0 bg-gray-50 h-fit border-gray-150 relative">
        <div
          className="md:w-2/3 w-full pt-2 md:pt-12 flex flex-col gap-4 border-r-2 border-b-2 border-gray-150 relative overflow-y-auto"
          ref={containerRef}
        >
          <ResearchContent
            textExample={selectedChat}
            searchFunction={handleSearch}
            isLatestResponse={false}
            response={selectedDocumentsChat}
          />
        </div>
        <DocumentPagination response={selectedDocumentsChat} loading={loading} />
      </div>
    );
  }

  return (
    <>
      {searchResponses.map((response, respIndex) => {
        const isLastInList = respIndex === searchResponses.length - 1;
        const hasContent = !!response.aiContent;
        const responseId = response.aiContent?.id || `response-${respIndex}`;

        const alreadyAnimated = animatedResponses.has(responseId);
        const shouldAnimate = isLastInList && hasContent && !loading && !alreadyAnimated;

        return (
          <div
            key={respIndex}
            ref={isLastInList ? latestSearchResultRef : null}
            className="flex md:flex-row flex-col-reverse w-full gap-0 bg-gray-50 h-fit border-gray-150 relative"
          >
            <div
              className={
                `md:w-2/3 w-full pt-2 md:pt-12 md:pb-24 ${isLastInList && !response.aiContent ? "pb-28" : "pb-0"
                } flex flex-col gap-4 border-r-2 ${isLastInList ? "" : "border-b-2"
                } border-gray-150 relative`
              }
            >
              {response.aiContent ? (
                <ResearchContent
                  textExample={response.aiContent}
                  searchFunction={handleSearch}
                  isLatestResponse={shouldAnimate}
                  response={response}
                />
              ) : (
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-6 md:px-12">
                  <div className="col-span-full lg:col-span-6 xl:col-span-4 flex flex-col gap-2 mb-4">
                    <Typography size="48" family="serif" weight="sb">
                      {currentPrompt || ""}
                    </Typography>
                    <AnimatedSearchText isMobile={isMobile} />
                  </div>
                  <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-44" />
                  <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-10" />
                  <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-[20rem] md:h-[50rem]" />
                  <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-44" />
                  <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-10" />
                </div>
              )}
            </div>
            <DocumentPagination response={response} loading={!response.aiContent} />
          </div>
        );
      })}

    </>
  );
});